import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const FinalDocument = ({ answers, isAdmin = false, onClose = null }) => {
  const [copied, setCopied] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const certificateRef = useRef(null);

  const handleCopy = () => {
    const text = `THE COMMITMENT\nDomain: ${answers.domain}\n\n1. The Exact Desire:\n${answers.desire}\n\n2. In Exchange, I Will Give:\n${answers.exchange}\n\n3. Beginning Today, I Will:\n${answers.plan}\n\n4. And I Will Achieve This By:\n${answers.deadline}\n\n5. I Will Speak This Commitment Aloud Every Day, Until It Manifests:\n${answers.ritual}\n\nOath: "${answers.oath}"`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  /**
   * Rafter Reliability Finding: PDF Generation UI Hang
   * Fixed by implementing a timeout and explicitly managing canvas memory.
   */
  const handleDownloadPDF = async () => {
    if (!certificateRef.current || isExporting) return;
    setIsExporting(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s limit
    
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2, // Optimized from 3 to prevent memory spikes on mobile
        useCORS: true,
        backgroundColor: '#FCF9F2',
        logging: false,
        removeContainer: true, // Cleanup DOM clones immediately
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.85); // JPEG 0.85 is smaller/faster than PNG
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const printableWidth = pdfWidth - (margin * 2);
      const imgHeight = (canvas.height * printableWidth) / canvas.width;
      
      pdf.addImage(imgData, 'JPEG', margin, margin, printableWidth, imgHeight);
      pdf.save(`Decree-${(answers.domain || 'Commitment').replace(/[^a-z0-9]/gi, '-')}.pdf`);
    } catch (error) {
      console.error('Rafter-Flagged Export Error:', error);
      alert('Export failed. Please try the "Print" option instead.');
    } finally {
      clearTimeout(timeoutId);
      setIsExporting(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="w-full mx-auto py-8 px-4 flex flex-col items-center"
    >
      <div className="text-center mb-10 print:hidden w-full max-w-3xl">
        <h1 className="font-serif text-4xl text-amber-500 mb-2">Manifestation Sealed</h1>
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <button 
            onClick={handleDownloadPDF} 
            disabled={isExporting}
            className="px-8 py-3 bg-amber-600 text-white rounded shadow-lg flex items-center gap-2 disabled:opacity-50"
          >
            {isExporting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SafeIcon icon={FiIcons.FiDownload} />}
            {isExporting ? 'Hardening Decree...' : 'Download PDF'}
          </button>
          
          <button 
            onClick={handleCopy}
            className="px-6 py-3 bg-neutral-800 text-neutral-300 rounded flex items-center gap-2 hover:bg-neutral-700 transition-colors"
          >
            <SafeIcon icon={copied ? FiIcons.FiCheck : FiIcons.FiCopy} />
            {copied ? 'Copied' : 'Copy Text'}
          </button>

          <button 
            onClick={handlePrint} 
            className="px-6 py-3 bg-neutral-800 text-neutral-300 rounded flex items-center gap-2 hover:bg-neutral-700 transition-colors"
          >
            <SafeIcon icon={FiIcons.FiPrinter} />
            Print
          </button>
          
          {onClose && (
            <button 
              onClick={onClose} 
              className="px-6 py-3 bg-neutral-900 text-neutral-500 rounded border border-neutral-800 hover:bg-neutral-800 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiIcons.FiX} />
            </button>
          )}
        </div>
      </div>

      {/* Certificate Content - Restructured for Unified Oath flow */}
      <div 
        ref={certificateRef} 
        className="bg-[#FCF9F2] p-1 relative shadow-2xl w-full max-w-[816px] min-h-[1056px] flex flex-col border border-stone-200"
      >
        <div className="flex-grow border-[12px] border-[#D4AF37] p-8 md:p-12 flex flex-col items-center text-center">
          <SafeIcon icon={FiIcons.FiFeather} className="text-3xl text-amber-700 mb-4" />
          <h1 className="text-3xl md:text-4xl font-serif tracking-widest uppercase text-stone-800 mb-2">THE COMMITMENT</h1>
          <p className="text-lg md:text-xl font-serif italic text-stone-700 mb-4">A Daily Return to the Person You Are Becoming</p>
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-amber-700 mb-8">{answers.domain}</h2>
          
          {/* Main Oath Declaration */}
          <p className="text-stone-900 font-serif italic text-xl md:text-2xl mb-12 max-w-2xl leading-relaxed">
            "{answers.oath}"
          </p>

          {/* Flowing Unified Document Details */}
          <div className="flex flex-col gap-6 text-center w-full px-2 max-w-3xl mx-auto font-serif">
            
            <div className="space-y-1">
              <span className="block text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-amber-700 font-sans">The Exact Desire</span>
              <p className="text-stone-800 text-base md:text-lg leading-relaxed">{answers.desire}</p>
            </div>
            
            <div className="space-y-1">
              <span className="block text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-amber-700 font-sans">In Exchange, I Will Give</span>
              <p className="text-stone-800 text-base md:text-lg leading-relaxed">{answers.exchange}</p>
            </div>

            <div className="space-y-1">
              <span className="block text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-amber-700 font-sans">Beginning Today, I Will</span>
              <p className="text-stone-800 text-base md:text-lg leading-relaxed">{answers.plan}</p>
            </div>

            <div className="space-y-1">
              <span className="block text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-amber-700 font-sans">And I Will Achieve This By</span>
              <p className="text-stone-800 text-lg md:text-xl font-bold">{answers.deadline}</p>
            </div>

            <div className="space-y-1">
              <span className="block text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-amber-700 font-sans">I Will Speak This Commitment Aloud Every Day, Until It Manifests</span>
              <p className="text-stone-800 text-base md:text-lg">{answers.ritual}</p>
            </div>
            
          </div>

          <div className="mt-auto pt-10">
            <p className="text-[8px] md:text-[9px] text-stone-400 uppercase tracking-[0.5em]">Non-Negotiable • Absolute • Final</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FinalDocument;